import { formatInvoiceQuantity } from './invoiceFormat';

const money = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '0.00';
  return v.toFixed(2);
};

const fmtDate = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d.slice(0, 10) : d;
  try {
    return new Date(s + 'T12:00:00').toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return String(d);
  }
};

/**
 * Minimal standalone HTML for download / print (matches invoice preview content).
 */
export function buildInvoiceHtmlDocument(invoice, vendorProfile = {}) {
  const vp = vendorProfile || {};
  const lines = invoice.line_items?.length
    ? invoice.line_items
    : [
        {
          description: 'Invoice total',
          quantity: 1,
          unit_price: invoice.subtotal ?? invoice.total_amount,
        },
      ];

  const subtotal = Number(invoice.subtotal ?? 0);
  const tax = Number(invoice.tax_amount ?? 0);
  const total = Number(invoice.total_amount ?? 0);
  const taxRate = invoice.tax_rate != null ? Number(invoice.tax_rate) : null;

  const client = invoice.client || {};
  const vendorLines = [
    vp.address,
    [vp.city, vp.state, vp.zip_code].filter(Boolean).join(', '),
    vp.country,
    vp.business_phone,
  ]
    .filter(Boolean)
    .map((line) => `<div>${escapeHtml(line)}</div>`)
    .join('');

  const rows = lines
    .map(
      (row) => `
    <tr>
      <td style="padding:10px;border-bottom:1px solid #eee;">${escapeHtml(row.description || '')}</td>
      <td style="padding:10px;border-bottom:1px solid #eee;text-align:right;">${formatInvoiceQuantity(row.quantity)}</td>
      <td style="padding:10px;border-bottom:1px solid #eee;text-align:right;">$${money(row.unit_price)}</td>
      <td style="padding:10px;border-bottom:1px solid #eee;text-align:right;">$${money(
        Number(row.quantity) * Number(row.unit_price)
      )}</td>
    </tr>`
    )
    .join('');

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"/><title>Invoice ${escapeHtml(invoice.invoice_number || '')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, sans-serif; margin: 0; padding: 0; color: #111; background: #fff; }
  .invoice-sheet { max-width: 800px; margin: 0 auto; padding: 40px 48px 48px; }
  h1 { font-size: 1.25rem; margin: 0 0 8px; }
  .muted { color: #666; font-size: 0.875rem; }
  table { width: 100%; border-collapse: collapse; margin-top: 16px; }
  th { text-align: left; font-size: 0.75rem; text-transform: uppercase; color: #666; padding: 10px 12px; border-bottom: 2px solid #ddd; }
  th:first-child, td:first-child { padding-left: 0; }
  th:last-child, td:last-child { padding-right: 0; }
  .totals { margin-top: 20px; max-width: 280px; margin-left: auto; padding-right: 4px; }
  .totals div { display: flex; justify-content: space-between; padding: 6px 0; }
  .grand { font-weight: 700; font-size: 1.1rem; border-top: 2px solid #111; padding-top: 10px; margin-top: 8px; }
</style></head><body>
  <div class="invoice-sheet">
  <p class="muted">Invoice</p>
  <h1>${escapeHtml(vp.business_name || 'Invoice')}</h1>
  ${vendorLines}
  <p style="margin-top:24px;"><strong>Invoice #</strong> ${escapeHtml(invoice.invoice_number || '')} &nbsp;·&nbsp; <strong>Status</strong> ${escapeHtml(
    invoice.status || ''
  )}</p>
  <div style="display:flex;justify-content:space-between;margin-top:24px;gap:24px;">
    <div>
      <div class="muted" style="margin-bottom:6px;">Bill to</div>
      <div><strong>${escapeHtml(client.name || '')}</strong></div>
      ${client.email ? `<div>${escapeHtml(client.email)}</div>` : ''}
      ${client.phone ? `<div>${escapeHtml(client.phone)}</div>` : ''}
    </div>
    <div style="text-align:right;">
      <div>Invoice date: <strong>${fmtDate(invoice.invoice_date)}</strong></div>
      <div>Due date: <strong>${fmtDate(invoice.due_date)}</strong></div>
    </div>
  </div>
  <table>
    <thead><tr>
      <th>Description</th><th style="text-align:right;">Qty</th><th style="text-align:right;">Rate</th><th style="text-align:right;">Amount</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="totals">
    <div><span>Subtotal</span><span>$${money(subtotal)}</span></div>
    <div><span>Tax${taxRate != null && taxRate > 0 ? ` (${taxRate}%)` : ''}</span><span>$${money(tax)}</span></div>
    <div class="grand"><span>Total</span><span>$${money(total)}</span></div>
  </div>
  ${
    invoice.payment_terms
      ? `<p style="margin-top:24px;"><strong>Payment terms</strong><br/>${escapeHtml(invoice.payment_terms)}</p>`
      : ''
  }
  ${invoice.notes ? `<p style="margin-top:16px;"><strong>Notes</strong><br/>${escapeHtml(invoice.notes)}</p>` : ''}
  </div>
</body></html>`;
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

